import React, { useState, useRef, useEffect } from 'react';
import DocChatUploadPanel from '../components/DocChatUploadPanel';
import DocChatMessage from '../components/DocChatMessage';
import bedrockService from '../services/bedrockService';
import fileService from '../services/fileService';

/**
 * DocumentChatPage — upload 1–5 documents and have a multi-turn
 * conversation about their content using AWS Bedrock.
 *
 * Phase 1 (upload): The user selects 1–5 files via DocChatUploadPanel.
 *   Clicking "Start Chat" uploads them to the backend's temporary S3
 *   prefix and transitions to Phase 2.
 *
 * Phase 2 (chat): The user types questions in the input bar at the
 *   bottom; each AI response is rendered as a DocChatMessage card with
 *   copy, export-PDF, and export-DOC controls.  Conversation history is
 *   maintained client-side and sent on every request so the model has
 *   full multi-turn context.
 *
 * The session can be reset at any time via the "New Session" button,
 * which cleans up the S3 documents and returns to Phase 1.
 */
const DocumentChatPage = () => {
  // Phase: 'upload' | 'chat'
  const [phase, setPhase] = useState('upload');

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Chat session state
  const [sessionId, setSessionId] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  /**
   * messages: Array of { role: 'user'|'assistant', content: string, sources?: string[] }
   */
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState('');
  const [querying, setQuerying] = useState(false);
  const [queryError, setQueryError] = useState('');

  const chatBottomRef = useRef(null);

  // Scroll to newest message whenever messages change
  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ------------------------------------------------------------------
  // Phase 1 → 2: Upload documents via the file microservice
  // ------------------------------------------------------------------
  const handleUpload = async (files) => {
    setUploading(true);
    setUploadError('');
    try {
      const sessionId = crypto.randomUUID();
      const uploadedNames = [];

      for (const file of files) {
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const customPath = `temp-doc-chat/${sessionId}/${safeName}`;
        await fileService.uploadFileToPath(file, customPath);
        uploadedNames.push(safeName);
      }

      setSessionId(sessionId);
      setUploadedFiles(uploadedNames);
      setMessages([]);
      setPhase('chat');
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        'Upload failed. Please try again.';
      setUploadError(detail);
    } finally {
      setUploading(false);
    }
  };

  // ------------------------------------------------------------------
  // Send a question to Bedrock (async job + poll)
  // ------------------------------------------------------------------
  const handleSend = async () => {
    const trimmed = question.trim();
    if (!trimmed || querying) return;

    // Append the user turn immediately for instant feedback
    const userMsg = { role: 'user', content: trimmed };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setQuestion('');
    setQuerying(true);
    setQueryError('');

    // Build the history array (all prior turns, NOT including the current question)
    const history = messages.map((m) => ({ role: m.role, content: m.content }));

    try {
      // Submit and get job_id back immediately (202)
      const { job_id } = await bedrockService.submitDocChatQuery(sessionId, trimmed, history);

      // Poll until completed or failed
      const POLL_INTERVAL_MS = 2000;
      const MAX_WAIT_MS = 120000;
      const deadline = Date.now() + MAX_WAIT_MS;

      let result = null;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        const statusData = await bedrockService.getDocChatJobStatus(job_id);
        if (statusData.status === 'completed') {
          result = statusData;
          break;
        }
        if (statusData.status === 'failed') {
          throw new Error(statusData.error || 'Query failed');
        }
      }

      if (!result) {
        throw new Error('Response timed out. Please try again.');
      }

      setMessages([
        ...nextMessages,
        { role: 'assistant', content: result.answer, sources: result.sources || [] },
      ]);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        'Query failed. Please try again.';
      setQueryError(detail);
      // Remove the optimistically-added user message on failure
      setMessages(messages);
      setQuestion(trimmed);
    } finally {
      setQuerying(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ------------------------------------------------------------------
  // Reset session
  // ------------------------------------------------------------------
  const handleNewSession = async () => {
    if (sessionId) {
      // Best-effort cleanup; lifecycle rule handles it anyway
      bedrockService.deleteTempDocSession(sessionId).catch(() => {});
    }
    setSessionId(null);
    setUploadedFiles([]);
    setMessages([]);
    setQuestion('');
    setQueryError('');
    setUploadError('');
    setPhase('upload');
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <div className="container-fluid py-3" style={{ maxWidth: 900 }}>
      {/* ── Header ── */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h4 className="mb-0">
            <i className="bi bi-chat-left-text me-2 text-primary" />
            Document Chat
          </h4>
          <p className="text-muted small mb-0">
            Upload up to 5 documents and ask questions about their content. These documents will not live permanently in our system and will be deleted after your session ends.
          </p>
        </div>
        {phase === 'chat' && (
          <button className="btn btn-outline-secondary btn-sm" onClick={handleNewSession}>
            <i className="bi bi-arrow-counterclockwise me-1" />
            New Session
          </button>
        )}
      </div>

      {/* ── Phase 1: Upload ── */}
      {phase === 'upload' && (
        <div className="card shadow-sm">
          <div className="card-body p-4">
            <h6 className="card-title mb-3">
              <i className="bi bi-upload me-2" />
              Select Documents
            </h6>
            <DocChatUploadPanel onUpload={handleUpload} uploading={uploading} />
            {uploadError && (
              <div className="alert alert-danger mt-3 py-2 small">
                <i className="bi bi-exclamation-circle me-1" />
                {uploadError}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Phase 2: Chat ── */}
      {phase === 'chat' && (
        <>
          {/* Uploaded files pill bar */}
          <div className="d-flex flex-wrap gap-2 mb-3">
            {uploadedFiles.map((name) => (
              <span key={name} className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 d-flex align-items-center gap-1" style={{ fontSize: '0.8rem' }}>
                <i className="bi bi-file-earmark-text" />
                {name}
              </span>
            ))}
          </div>

          {/* Message thread */}
          <div
            className="mb-3"
            style={{ minHeight: 200, maxHeight: '60vh', overflowY: 'auto' }}
          >
            {messages.length === 0 && (
              <div className="text-center text-muted py-5">
                <i className="bi bi-chat-dots fs-2 d-block mb-2 opacity-50" />
                <p className="mb-0">Ask your first question below&hellip;</p>
              </div>
            )}

            {messages.map((msg, idx) => {
              if (msg.role === 'user') {
                return (
                  <div key={idx} className="d-flex justify-content-end mb-2">
                    <div
                      className="bg-primary text-white rounded px-3 py-2"
                      style={{ maxWidth: '75%', whiteSpace: 'pre-wrap' }}
                    >
                      {msg.content}
                    </div>
                  </div>
                );
              }

              return (
                <DocChatMessage
                  key={idx}
                  index={idx}
                  answer={msg.content}
                  sources={msg.sources || []}
                />
              );
            })}

            {/* Thinking indicator */}
            {querying && (
              <div className="d-flex align-items-center gap-2 text-muted small py-2 ps-1">
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                Analyzing documents…
              </div>
            )}

            {/* Query error */}
            {queryError && (
              <div className="alert alert-danger py-2 small">
                <i className="bi bi-exclamation-circle me-1" />
                {queryError}
              </div>
            )}

            {/* Auto-scroll anchor */}
            <div ref={chatBottomRef} />
          </div>

          {/* Input bar */}
          <div className="card shadow-sm">
            <div className="card-body p-2">
              <div className="d-flex gap-2">
                <textarea
                  className="form-control form-control-sm"
                  rows={2}
                  placeholder="Ask a question about your documents… (Shift+Enter for newline)"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={querying}
                  style={{ resize: 'none' }}
                />
                <button
                  className="btn btn-primary align-self-end"
                  onClick={handleSend}
                  disabled={querying || !question.trim()}
                  title="Send (Enter)"
                >
                  {querying ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  ) : (
                    <i className="bi bi-send" />
                  )}
                </button>
              </div>
              <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.7rem' }}>
                Press <kbd>Enter</kbd> to send · <kbd>Shift+Enter</kbd> for a new line
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DocumentChatPage;
